import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fadeUp, viewport } from '../utils/animations';

// ─── OPTIMIZED LAZY VIDEO ────────────────────────────────────────────────────
const LazyVideo = memo(function LazyVideo({ src, autoPlay, className, ...props }) {
  const videoRef = useRef(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!hasLoaded) setHasLoaded(true);
          if (autoPlay && videoRef.current) {
            videoRef.current.play().catch(() => {});
          }
        } else {
          if (autoPlay && videoRef.current) {
            videoRef.current.pause();
          }
        }
      },
      { rootMargin: '600px' }
    );

    const el = videoRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [autoPlay, hasLoaded]);

  return (
    <video
      ref={videoRef}
      src={hasLoaded ? src : undefined}
      className={className}
      autoPlay={autoPlay && hasLoaded}
      {...props}
    />
  );
});

// ─── FAQ DATA ────────────────────────────────────────────────────────────────
const faqs = [
  { title: "What is included with my booking?", body: "All sessions are completely turnkey. Your booking includes your designated set, 4K cinema cameras, professional studio lighting, broadcast-quality audio, and a dedicated operator to run the entire production. You only need to bring yourself and your guest." },
  { title: "What is the maximum guest capacity?", body: "Capacity is strictly enforced based on your selected set to ensure pristine audio and perfect camera framing. Luxe holds up to 4 people. Nova and Echo hold up to 2 people. Vault is strictly a 1-person set." },
  { title: "How do I receive my video files?", body: "To take your raw files home immediately for free, you must bring a high-speed portable solid-state drive (SSD). If you do not bring a hard drive, you must select the Cloud File Delivery add-on at checkout to receive a download link within 5 business days (raw 4K files are massive and take time to upload). If you purchase post-production editing, your final polished videos will be delivered to you digitally. However, we still highly recommend bringing an SSD to secure your original, unedited raw footage on the same day." },
  { title: "Do you offer post-production and video editing?", body: "Yes. We offer two distinct post-production add-ons for podcast sessions: Full Podcast Episode Edit and Social Media Clips. For full episodes, our team handles the multi-camera cuts, audio cleanup, and title cards. For social clips, we pull the best moments and reframe them into vertical formats. Final polished assets are delivered within 7 to 10 days, depending on the scope of the project. (Note: Post-production for non-video podcast content is quoted separately)." },
  { title: "Can I record multiple episodes in a single booking?", body: "Yes. You can record as many segments or episodes as you like within your reserved time block. However, please note that our editing services are strictly per-episode. If you record two episodes in one session and want both of them edited, you must select a quantity of \"2\" for your editing add-ons at checkout." },
  { title: "What is your cancellation and rescheduling policy?", body: "We require a minimum of 24 hours notice for a full refund. If you cancel with less than 24 hours notice, the original booking fee is non-refundable, but we will apply it toward rescheduling your session for a future date." },
  { title: "Where are you located?", body: "We are based in Fremont, CA. Ample parking is available directly in our private lot alongside convenient street parking." },
  { title: "When should I arrive?", body: "Please arrive exactly 15 minutes before your scheduled block. This provides enough time to get comfortable, review the workflow, and get situated before we hit record." },
  { title: "Can I bring food or drinks into the studio?", body: "Food and beverages are welcome, provided they are kept away from all technical equipment. For the integrity of the studio and the comfort of all clients, the facility is strictly an alcohol-free and smoke-free environment." },
  { title: "Can I bring a remote guest into my session?", body: "Yes. Select the Virtual Guest add-on at checkout. You must have your remote meeting fully set up and provide us with your active Riverside, StreamYard, or Zoom link at least 24 hours before your shoot. We route your guest directly onto an in-studio screen so you can maintain natural eye contact while we record your separate, high-quality audio and video locally." },
  { title: "Do you shoot on location?", body: "Yes. We offer comprehensive on-location production. Our team brings our cinema cameras, lighting arrays, and audio equipment directly to your office or event space to deliver the exact same high-end standard." },
  { title: "Do you offer in-person studio tours?", body: "Unfortunately, due to strict timing constraints and to protect the privacy of our active recording clients, we operate a closed set and do not offer in-person studio tours. However, all photos, video walkthroughs, and client examples on our website are 100% accurate. Please feel free to review our portfolio online; the premium standard you see is exactly what you will experience in person." }
];

// ─── FAQPAGE JSON-LD ─────────────────────────────────────────────────────────
const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ title, body }) => ({
    '@type': 'Question',
    name: title,
    acceptedAnswer: { '@type': 'Answer', text: body },
  })),
};

// ─── ASSET HELPERS ────────────────────────────────────────────────────────────
const V = {
  vault1:      'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/900e83dfb975cb28b656aa0968a4457b.mov',
  echo1:       'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/217f675a492af5ba54ab4f6aeda291eb.mov',
  nova1:       'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/9bdcdab02d2b0907787e1f77c778e0bf.mov',
  luxe1:       'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vbXA0/e917a7a1f1b3e53157754d819648bd5a.mp4',
  vsl1:        'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/520b7dd497bd8f17457763457032b939.mov',
  tiffloop:    'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/95530c91dbffc6c3c967a97f9e34e027.mov',
  kelly1:      'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/bdec17e80fccd1e86f13a1098f584e7f.mov',
  echo2:       'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/428fff1a42001f225e86464b93b9d343.mov',
  azita:       'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/ef2c4958151430faa4e76874daf93a23.mov',
  natty:       'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/04080691f08fd5b7d76036c5e0b6d7ec.mov',
  looplux:     'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/23d5ab55ac287f418b61a7e7c0777519.mov',
  interviewlop:'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/videos/dmlkZW8vcXVpY2t0aW1l/05c20469eb9d7900bf8404da1dd40aa8.mov',
};
const P = {
  vault1:  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/34d2ea00f9857bd86bb74153c0d48f40.webp',
  vault2:  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvcG5n/327342bc5001060c1a1b2e47f9a8323b.png',
  vault3:  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/74bec9e0b20a218892079ee2b1036c76.webp',
  echo1:   'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvanBlZw==/2b6127be4855d400bae43a70b2c61e1f.jpg',
  nova1:   'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/eb855d3a16028d6551d2925d044cd1c7.webp',
  luxe1:   'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2UvanBlZw==/b157a458a787e2df8fa6558ea731bfc6.jpg',
  kelly1:  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/a9fb0e3b4e68999f11e0965f60e9f063.webp',
  kelly2:  'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/e05f315e740880642ab9e5910eeeca71.webp',
  nova2:   'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/4196a3e2f41d1baedf2e7e87f0d87309.webp',
  luxe2:   'https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/599553c5da67abd10229fee08be1c150.webp',
};

// ─── SETS DATA ────────────────────────────────────────────────────────────────
const sets = [
  {
    id: 'vault', number: '01', name: 'VAULT',
    capacity: '1 Person', use: 'Solo Podcasts, VSLs & Executive Messages',
    specs: '4K CINEMA CAMS | STUDIO LIGHTING | PRO MICS',
    alt: 'Vault Set at Bay Area Podcast Studio — solo podcast studio in Fremont, CA',
    video: V.vault1, image: P.vault1,
    lookbook: [
      { type: 'video', src: V.vault1,   alt: 'Vault Set ambient video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.vault1,   alt: 'Vault Set still — solo podcast studio Fremont CA' },
      { type: 'video', src: V.vsl1,     alt: 'Vault Set VSL video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.vault2,   alt: 'Vault Set talent still — Bay Area Podcast Studio' },
      { type: 'video', src: V.tiffloop, alt: 'Vault Set talent video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.vault3,   alt: 'Vault Set detail still — Bay Area Podcast Studio' },
    ]
  },
  {
    id: 'echo', number: '02', name: 'ECHO',
    capacity: '2 Guests', use: '1-on-1 Interviews & Co-Hosted Shows',
    specs: '4K CINEMA CAMS | STUDIO LIGHTING | PRO MICS',
    alt: 'Echo Set at Bay Area Podcast Studio — 2-person interview studio in Fremont, CA',
    video: V.echo1, image: P.echo1,
    lookbook: [
      { type: 'video', src: V.echo1,  alt: 'Echo Set ambient video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.echo1,  alt: 'Echo Set still — 2-person interview studio Fremont CA' },
      { type: 'video', src: V.kelly1, alt: 'Echo Set talent video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.kelly2, alt: 'Echo Set talent still — Bay Area Podcast Studio' },
      { type: 'video', src: V.echo2,  alt: 'Echo Set second video loop — Bay Area Podcast Studio' },
    ]
  },
  {
    id: 'nova', number: '03', name: 'NOVA',
    capacity: '2 Guests', use: '1-on-1 Interviews & Co-Hosted Shows',
    specs: '4K CINEMA CAMS | STUDIO LIGHTING | PRO MICS',
    alt: 'Nova Set at Bay Area Podcast Studio — co-hosted podcast studio in Fremont, CA',
    video: V.nova1, image: P.nova1,
    lookbook: [
      { type: 'video', src: V.nova1, alt: 'Nova Set ambient video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.nova1, alt: 'Nova Set still — co-hosted podcast studio Fremont CA' },
      { type: 'video', src: V.azita, alt: 'Nova Set Azita talent video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.nova2, alt: 'Nova Set talent still — Bay Area Podcast Studio' },
      { type: 'video', src: V.natty, alt: 'Nova Set Natty talent video loop — Bay Area Podcast Studio' },
    ]
  },
  {
    id: 'luxe', number: '04', name: 'LUXE',
    capacity: 'Up to 4 Guests', use: 'Panel Discussions & Group Podcasts',
    specs: '4K CINEMA CAMS | STUDIO LIGHTING | PRO MICS',
    alt: 'Luxe Set at Bay Area Podcast Studio — 4-person panel podcast studio in Fremont, CA',
    video: V.luxe1, image: P.luxe1,
    lookbook: [
      { type: 'video', src: V.luxe1,        alt: 'Luxe Set ambient video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.luxe1,        alt: 'Luxe Set still — panel podcast studio Fremont CA' },
      { type: 'video', src: V.looplux,      alt: 'Luxe Set talent video loop — Bay Area Podcast Studio' },
      { type: 'photo', src: P.luxe2,        alt: 'Luxe Set talent still — Bay Area Podcast Studio' },
      { type: 'video', src: V.interviewlop, alt: 'Luxe Set interview video loop — Bay Area Podcast Studio' },
    ]
  }
];

// ─── SET CARD ─────────────────────────────────────────────────────────────────
function SetCard({ set, onOpen }) {
  return (
    <div>
      <div
        className="relative aspect-video w-full overflow-hidden border-2 border-black bg-neutral-900 group cursor-pointer mb-5"
        onClick={() => onOpen(set)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(set)}
        aria-label={`Open ${set.name} lookbook`}
      >
        <LazyVideo
          autoPlay loop muted playsInline
          className="w-full h-full object-cover absolute inset-0 z-0"
          src={set.video}
        />
        <img
          src={set.image}
          alt={set.alt}
          className="w-full h-full object-cover transition-opacity duration-500 ease-in-out group-hover:opacity-0 absolute inset-0 z-10"
        />
      </div>

      <span className="text-sm font-mono font-black tracking-widest text-black mb-1 block">
        SET {set.number} // {set.name}
      </span>
      <h3 className="text-base md:text-lg font-black text-black mb-2">
        {set.capacity} // {set.use}
      </h3>
      <p className="text-xs font-mono text-black font-bold uppercase mb-4">{set.specs}</p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => onOpen(set)}
          className="text-xs font-bold uppercase tracking-[0.15em] py-3.5 px-6 bg-white text-black border-2 border-black hover:bg-neutral-100 transition-colors duration-150 cursor-pointer w-full sm:w-auto text-center"
        >
          VIEW {set.name}
        </button>
      </div>
    </div>
  );
}

// ─── LOOKBOOK SLIDE ───────────────────────────────────────────────────────────
function LookbookSlide({ asset, active }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current) return;
    if (active) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [active]);

  if (asset.type === 'video') {
    return (
      <video
        ref={videoRef}
        src={asset.src}
        muted
        loop
        playsInline
        autoPlay={active}
        aria-label={asset.alt}
        className="w-full h-full object-cover absolute inset-0"
      />
    );
  }
  return (
    <img
      src={asset.src}
      alt={asset.alt}
      className="w-full h-full object-cover absolute inset-0"
    />
  );
}

// ─── SPEC SEPARATOR ──────────────────────────────────────────────────────────
function SpecSep() {
  return <span className="text-white/20 mx-2 md:mx-3 select-none">•</span>;
}

// ─── LOOKBOOK MODAL ───────────────────────────────────────────────────────────
function LookbookModal({ set, onClose }) {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const touchStartX = useRef(null);
  const total = set.lookbook.length;

  const go = useCallback((next, direction) => {
    setDir(direction);
    setIndex(next);
  }, []);

  const prev = () => go((index - 1 + total) % total, -1);
  const next = () => go((index + 1) % total, 1);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, index]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) delta > 0 ? next() : prev();
    touchStartX.current = null;
  };

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0 }),
  };

  const pad = (n) => String(n + 1).padStart(2, '0');

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-[5vw]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          className="relative w-full max-w-5xl bg-[#0a0a0a] flex flex-col overflow-hidden border border-white/10"
          style={{ maxHeight: '85vh' }}
          initial={{ opacity: 0, scale: 0.97, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 12 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── MODAL HEADER ── */}
          <div className="shrink-0 border-b border-white/10">
            <div className="flex items-center justify-between px-5 md:px-7 pt-3.5 pb-3">
              <div className="flex items-baseline gap-3">
                <span className="text-[10px] font-mono font-black tracking-[0.25em] text-zinc-500 uppercase">
                  SET {set.number}
                </span>
                <h2 className="text-xs md:text-sm font-black text-white tracking-[0.15em] uppercase leading-none">
                  {set.name} — LOOKBOOK
                </h2>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[11px] font-mono font-bold text-zinc-500 tracking-widest tabular-nums">
                  {pad(index)} / {pad(total - 1)}
                </span>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center border border-white/15 text-white hover:bg-white/10 transition-colors shrink-0"
                  aria-label="Close lookbook"
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-base" />
                </button>
              </div>
            </div>
            <div className="px-5 md:px-7 pb-3 flex items-center flex-wrap gap-y-1">
              <span className="text-[10px] font-mono font-black tracking-[0.18em] text-zinc-400 uppercase">CAPACITY:</span>
              <span className="text-[10px] font-mono font-black tracking-[0.18em] text-white uppercase ml-1.5">{set.capacity}</span>
              <SpecSep />
              <span className="text-[10px] font-mono font-black tracking-[0.18em] text-zinc-400 uppercase">BEST FOR:</span>
              <span className="text-[10px] font-mono font-black tracking-[0.18em] text-white uppercase ml-1.5">{set.use}</span>
              <SpecSep />
              <span className="text-[10px] font-mono font-black tracking-[0.18em] text-zinc-400 uppercase">STUDIO OPERATOR:</span>
              <span className="text-[10px] font-mono font-black tracking-[0.18em] text-white uppercase ml-1.5">INCLUDED</span>
            </div>
          </div>

          {/* ── 16:9 CAROUSEL STAGE ── */}
          <div
            className="relative w-full shrink-0 select-none overflow-hidden"
            style={{ aspectRatio: '16 / 9' }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.div
                key={index}
                custom={dir}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <LookbookSlide asset={set.lookbook[index]} active={true} />
              </motion.div>
            </AnimatePresence>

            <button
              onClick={prev}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center border border-white/20 bg-black/60 text-white hover:bg-black/90 transition-colors"
              aria-label="Previous slide"
            >
              <SafeIcon icon={FiIcons.FiChevronLeft} className="text-lg md:text-xl" />
            </button>

            <button
              onClick={next}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center border border-white/20 bg-black/60 text-white hover:bg-black/90 transition-colors"
              aria-label="Next slide"
            >
              <SafeIcon icon={FiIcons.FiChevronRight} className="text-lg md:text-xl" />
            </button>

            <div className="absolute bottom-3.5 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {set.lookbook.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i, i > index ? 1 : -1)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                    i === index ? 'bg-white scale-125' : 'bg-white/30 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>

        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── ACCORDION ────────────────────────────────────────────────────────────────
const AccordionItem = ({ title, body, isTop }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className={`border-b-2 border-black ${isTop ? 'border-t-2' : ''}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 md:py-6 text-left font-black text-base md:text-xl lg:text-2xl flex justify-between items-center bg-white hover:bg-neutral-50 transition-colors cursor-pointer group focus:outline-none"
        aria-expanded={isOpen}
      >
        <span className="pr-4 leading-snug">{title}</span>
        <span
          className={`text-2xl font-light text-black transform transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-45' : ''}`}
          aria-hidden="true"
        >+</span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="text-sm md:text-lg lg:text-xl font-medium leading-relaxed md:leading-loose text-black pt-3 pb-6 md:pb-8">{body}</p>
      </div>
    </div>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────
export default function StudioSessions() {
  const location = useLocation();
  const [activeSet, setActiveSet] = useState(null);

  // ── meta tags ──────────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = 'Studio Sessions & Sets | Bay Area Podcast Studio';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Book turnkey 4K video podcast studio sessions in Fremont, CA. Select from professional studio sets with an engineer included and instant raw 4K footage handoff.');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Studio Sessions & Sets | Bay Area Podcast Studio');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', 'Turnkey video podcast studio rentals in Fremont, CA. Engineer included and instant raw file handoff.');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://bayareapodcast.studio/studio-sessions');
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', 'Studio Sessions & Sets | Bay Area Podcast Studio');
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', 'Turnkey video podcast studio rentals in Fremont, CA. Engineer included and instant raw file handoff.');
    return () => {
      if (metaDesc) metaDesc.setAttribute('content', 'Premier turnkey video podcast studio in Fremont, CA. Serving San Francisco and Silicon Valley executive teams, founders, and creators with 4K multi-cam studio sessions and on-location video production.');
      if (ogTitle) ogTitle.setAttribute('content', 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio');
      if (ogDesc) ogDesc.setAttribute('content', 'Turnkey 4K video podcasting and commercial production studio in Fremont, CA.');
      if (ogUrl) ogUrl.setAttribute('content', 'https://bayareapodcast.studio/');
      if (twTitle) twTitle.setAttribute('content', 'Bay Area Podcast Studio | Premium Video Podcast & Content Studio');
      if (twDesc) twDesc.setAttribute('content', 'Turnkey 4K video podcasting and commercial production studio in Fremont, CA.');
    };
  }, []);

  // ── FAQPage JSON-LD ────────────────────────────────────────────────────────
  useEffect(() => {
    const SCRIPT_ID = 'ld-studio-sessions-faqpage';
    let el = document.getElementById(SCRIPT_ID);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = SCRIPT_ID;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(FAQ_JSON_LD);
    return () => {
      const tag = document.getElementById(SCRIPT_ID);
      if (tag) tag.remove();
    };
  }, []);

  useEffect(() => {
    if (location.state?.scrollToBooking) {
      const timer = setTimeout(() => scrollToBooking(), 600);
      window.history.replaceState({}, document.title);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://booking.bayareapodcast.studio/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { if (document.body.contains(script)) document.body.removeChild(script); };
  }, []);

  const scrollToBooking = () => {
    const el = document.getElementById('booking-engine');
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const el = document.getElementById(targetId);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 72;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <main className="pt-[72px] min-h-screen bg-[#FFFFFF] text-[#000000]">

      {/* 1. HERO */}
      <section className="relative w-full h-[80vh] min-h-[520px] overflow-hidden bg-black flex items-end justify-center text-center pb-10 md:pb-16">
        <img
          src="https://media-manager-c.questera.ai/greta-media/9c95a892991f8bee90ef212226d1d2edf0307c7f7bf17171a7a0344a5d4721a708ad3a9cb23f146c83b11da91425b5a8/images/aW1hZ2Uvd2VicA==/ea742bf3c68199a21a55a6c76e1e4313.webp"
          alt="Turnkey 4K video podcast studio interior at Bay Area Podcast Studio in Fremont, CA"
          className="absolute inset-0 w-full h-full object-cover object-[center_25%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
        <motion.div
          className="relative z-10 w-full max-w-4xl mx-auto px-5 md:px-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight max-w-3xl mx-auto mb-4 md:mb-5 leading-tight uppercase">
            FOUR DISTINCT SETS. ONE PREMIUM STANDARD.
          </h1>
          <p className="text-sm sm:text-base md:text-xl text-white font-bold max-w-2xl mx-auto mb-7 md:mb-8 leading-relaxed drop-shadow-md">
            Walk in, sit down, and record. Every set is pre-lit and dialed in so you can focus strictly on your show.
          </p>
          <a
            href="#set-showcase"
            onClick={(e) => handleSmoothScroll(e, 'set-showcase')}
            className="bg-white text-black font-bold text-xs md:text-sm tracking-[0.15em] uppercase py-4 px-8 w-full sm:w-auto rounded-none inline-block hover:bg-neutral-200 transition-colors duration-150 cursor-pointer"
          >
            VIEW SETS &amp; BOOK
          </a>
        </motion.div>
      </section>

      {/* 2. 3-STEP PROCESS STRIP */}
      <section className="w-full bg-[#FFFFFF] border-t-2 border-b-2 border-black py-12 md:py-16 px-5 md:px-12">
        <motion.div className="max-w-7xl mx-auto" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-14">
            <div>
              <span className="text-sm font-mono font-black tracking-widest text-black mb-3 block">01 //</span>
              <h3 className="text-base md:text-xl font-black tracking-tight text-black uppercase mb-3">SELECT SET &amp; ADD-ONS</h3>
              <p className="text-sm md:text-base font-medium text-black leading-relaxed">Choose your room and select optional post-production add-ons directly on the booking calendar.</p>
            </div>
            <div>
              <span className="text-sm font-mono font-black tracking-widest text-black mb-3 block">02 //</span>
              <h3 className="text-base md:text-xl font-black tracking-tight text-black uppercase mb-3">RECORD WITH AN OPERATOR</h3>
              <p className="text-sm md:text-base font-medium text-black leading-relaxed">We manage the cinema cameras, sound levels, and studio lighting while you execute your show. A studio operator is included in every session.</p>
            </div>
            <div>
              <span className="text-sm font-mono font-black tracking-widest text-black mb-3 block">03 //</span>
              <h3 className="text-base md:text-xl font-black tracking-tight text-black uppercase mb-3">INSTANT FILE HANDOFF</h3>
              <p className="text-sm md:text-base font-medium text-black leading-relaxed">Bring an SSD to walk out with your raw 4K footage immediately after your session wraps.</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 3. SET SHOWCASE GRID */}
      <section id="set-showcase" className="w-full bg-[#FFFFFF] py-12 md:py-24 px-5 md:px-12 border-b-2 border-black">
        <motion.div className="max-w-7xl mx-auto" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
          <div className="mb-10 md:mb-16 text-left">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-black tracking-tight uppercase">Select Your Set.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            {sets.map((set) => (
              <SetCard key={set.id} set={set} onOpen={setActiveSet} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* 4. BOOKING CALENDAR */}
      <section id="booking-engine" className="w-full bg-[#FFFFFF] py-12 md:py-24 px-5 md:px-12 border-b-2 border-black">
        <motion.div className="max-w-7xl mx-auto" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
          <div className="max-w-3xl mx-auto text-center mb-8 md:mb-14 px-2">
            <span className="text-xs md:text-sm font-mono font-black tracking-widest text-black mb-4 block uppercase">[ STUDIO OPERATOR INCLUDED IN EVERY SESSION ]</span>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-black tracking-tight uppercase mb-4">BOOK YOUR SESSION.</h2>
            <p className="text-sm md:text-base lg:text-lg font-bold text-black">Choose your session type, select date &amp; time, and pick any add-ons if needed.</p>
          </div>
          <div className="w-full max-w-5xl mx-auto border-2 border-black bg-white shadow-2xl relative overflow-x-auto">
            <div className="w-full min-h-[700px] min-w-[320px]">
              <div
                className="embedded-booking"
                data-url="https://booking.bayareapodcast.studio"
                data-query="&t=s&uuid=3e36b2fd-235f-4697-a049-116ea08bc1d7"
                data-lang="en"
                data-autoresize="0"
                data-showsidebar="1"
                data-showservices="0"
                style={{ minWidth: '320px', height: '768px', width: '100%' }}
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* 5. FAQ ACCORDION */}
      <section className="w-full bg-[#FFFFFF] py-16 md:py-32 px-5 md:px-12">
        <motion.div className="max-w-4xl mx-auto" initial="hidden" whileInView="visible" viewport={viewport} variants={fadeUp}>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-black text-black tracking-tight uppercase mb-10 md:mb-12 text-center">
            STUDIO POLICIES &amp; FAQS.
          </h2>
          <div>
            {faqs.map((faq, i) => (
              <AccordionItem key={i} title={faq.title} body={faq.body} isTop={i === 0} />
            ))}
          </div>

          <motion.div
            className="mt-14 md:mt-16 flex justify-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <button
              onClick={scrollToBooking}
              className="bg-black text-white font-black text-xs md:text-sm uppercase tracking-[0.15em] py-4 px-10 border-2 border-black hover:bg-neutral-800 transition-colors duration-150 cursor-pointer w-full sm:w-auto text-center"
              aria-label="Scroll back up to the booking calendar"
            >
              ↑ BACK TO BOOKING CALENDAR
            </button>
          </motion.div>
        </motion.div>
      </section>

      {activeSet && (
        <LookbookModal
          set={activeSet}
          onClose={() => setActiveSet(null)}
        />
      )}
    </main>
  );
}